import {getTestProps} from '@benchling/js-test-utils';
import {Anchor, Box, Breadcrumbs, Text, createStyles} from '@mantine/core';
import React from 'react';

import {isSimpleClick} from 'BenchlingUtil';

import type {DetailCrumb} from './detailCrumbs';

/**
 * A detail page's small-type trail: linked crumbs lead to the current, unlinked one.
 *
 * Every crumb is shown, however deep the trail, since detail hierarchies are shallow.
 */
export default function DetailBreadcrumbs({crumbs}: {crumbs: ReadonlyArray<DetailCrumb>}): JSX.Element {
  const {classes} = useStyles();
  return (
    <Box aria-label="Breadcrumb" component="nav">
      <Breadcrumbs
        classNames={{separator: classes.separator}}
        {...getTestProps({component: 'DetailBreadcrumbs', element: 'root'})}
      >
        {crumbs.map((crumb, index) =>
          'href' in crumb ? (
            <Anchor
              href={crumb.href}
              key={index}
              onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                // Modified and middle clicks fall through to the browser, e.g. to open a new tab.
                if (!isSimpleClick(event)) {
                  return;
                }
                event.preventDefault();
                crumb.onClick();
              }}
              size="sm"
              {...getTestProps({component: 'DetailBreadcrumbs', element: 'crumb'})}
            >
              {crumb.label}
            </Anchor>
          ) : (
            <Text
              aria-current={index === crumbs.length - 1 ? 'page' : undefined}
              color="dimmed"
              key={index}
              size="sm"
              {...getTestProps({component: 'DetailBreadcrumbs', element: 'crumb'})}
            >
              {crumb.label}
            </Text>
          )
        )}
      </Breadcrumbs>
    </Box>
  );
}

const useStyles = createStyles((theme) => ({
  separator: {
    color: theme.colors.gray[5],
  },
}));
